'use client'

import * as React from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '../../utils'

export interface BreadcrumbItem {
  /** Visible text. */
  label: string
  /**
   * Where this crumb links to. Omit for the current page (rendered as
   * non-interactive emphasised text).
   */
  href?: string
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[]
  /**
   * Custom link component (e.g. `next/link`). Receives `href`, `className`,
   * and `children`. Defaults to a plain `<a>` so the component stays
   * framework-agnostic.
   */
  linkAs?: React.ComponentType<{
    href: string
    className?: string
    children: React.ReactNode
  }>
  /**
   * What to render between crumbs. Defaults to a small chevron. Pass a
   * string (e.g. `'/'`) or a node to override.
   */
  separator?: React.ReactNode
}

/**
 * Compact breadcrumb trail used at the top of detail pages so users always
 * know where they are. Visual style matches the rest of the platform —
 * muted small text with a slightly bolder current crumb.
 *
 * Usage:
 *   <Breadcrumb
 *     items={[
 *       { label: 'Fleet',   href: '/fleet' },
 *       { label: 'Drivers', href: '/fleet/drivers' },
 *       { label: driver.fullname },
 *     ]}
 *   />
 */
export function Breadcrumb({
  items,
  linkAs: LinkAs,
  separator,
  className,
  ...props
}: BreadcrumbProps) {
  if (!items || items.length === 0) return null

  const sep = separator ?? <ChevronRight className="h-3 w-3 text-border" aria-hidden="true" />

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        'flex flex-wrap items-center gap-1.5 text-[11.5px] text-muted-foreground',
        className,
      )}
      {...props}
    >
      {items.map((item, i) => {
        const isLast = i === items.length - 1
        const linkClass = 'hover:text-foreground transition-colors rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'

        return (
          <React.Fragment key={`${item.label}-${i}`}>
            {i > 0 && (
              <span role="presentation" aria-hidden="true" className="inline-flex items-center text-border">
                {sep}
              </span>
            )}
            {item.href && !isLast ? (
              LinkAs ? (
                <LinkAs href={item.href} className={linkClass}>{item.label}</LinkAs>
              ) : (
                <a href={item.href} className={linkClass}>{item.label}</a>
              )
            ) : (
              <span
                className={cn(isLast && 'text-foreground font-medium')}
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </span>
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}
